/**
 * Career Expo 2026 — quick student registration (separate from main signup).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faGraduationCap,
  faCheck,
  faUser,
  faEnvelope,
  faPhone,
  faGlobe,
  faUsers,
  faGift,
  faWandMagicSparkles,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/FINAL LOGO AK.png';
import '../styles/ExpoStudentRegistration.css';

const HIGHLIGHTS = [
  {
    icon: faGift,
    title: '100% FREE for Everyone',
    desc: 'No hidden fees — jobs and internships at zero cost.',
    accent: 'orange',
  },
  {
    icon: faGlobe,
    title: "World's First Community-Enabled Job Portal",
    desc: 'Connect through your community. Get verified. Get hired faster.',
    accent: 'blue',
  },
  {
    icon: faUsers,
    title: 'Built for Kenya & Beyond',
    desc: 'AI-powered matching, smart resumes, and real opportunities.',
    accent: 'green',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ACCOUNT_TYPES = [
  { value: 'jobseeker', label: 'Jobseeker', icon: faBriefcase },
  { value: 'intern', label: 'Intern', icon: faGraduationCap },
  { value: 'employer', label: 'Employer', icon: faBuilding },
];

const getTypeIcon = (type) =>
  ACCOUNT_TYPES.find((t) => t.value === type)?.icon ?? faBriefcase;

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  type: 'jobseeker',
};

const RESET_DELAY_SEC = 5;

const ExpoStudentRegistration = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resetCountdown, setResetCountdown] = useState(RESET_DELAY_SEC);

  const resetForNewRegistration = useCallback(() => {
    setSuccess(false);
    setSuccessMessage('');
    setError('');
    setForm({ ...INITIAL_FORM });
    setResetCountdown(RESET_DELAY_SEC);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!success) return undefined;

    setResetCountdown(RESET_DELAY_SEC);
    const countdown = setInterval(() => {
      setResetCountdown((prev) => (prev > 1 ? prev - 1 : prev));
    }, 1000);

    const timer = setTimeout(() => {
      resetForNewRegistration();
    }, RESET_DELAY_SEC * 1000);

    return () => {
      clearInterval(countdown);
      clearTimeout(timer);
    };
  }, [success, resetForNewRegistration]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl('/api/expo/student-register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          type: form.type,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        const errMsg =
          data.message ||
          (data.error === 'already_registered'
            ? 'This email is already registered. Please log in with your existing account.'
            : data.error);
        setError(errMsg || 'Registration failed. Please try again.');
        return;
      }

      setSuccessMessage(
        data.message || 'Registration successful! Check your email and click Activate Account to set your password.'
      );
      setSuccess(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="expo-student-page">
      {/* Animated live background */}
      <div className="expo-bg" aria-hidden="true">
        <div className="expo-bg-gradient" />
        <div className="expo-bg-grid" />
        <div className="expo-orb expo-orb-1" />
        <div className="expo-orb expo-orb-2" />
        <div className="expo-orb expo-orb-3" />
        <div className="expo-orb expo-orb-4" />
        <div className="expo-float-particles">
          {Array.from({ length: 18 }).map((_, i) => (
            <span key={i} className="expo-particle" style={{ '--i': i }} />
          ))}
        </div>
      </div>

      <header className="expo-student-header">
        <motion.div
          className="expo-header-brand-panel"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="expo-header-brand-accent" aria-hidden="true" />
          <img src={logoImage} alt="AksharJobs" className="expo-student-logo" />
          <h1 className="expo-student-brand">AKSHARJOBS</h1>
          <p className="expo-student-tagline" aria-label="Connect, Discover, Elevate">
            <span className="expo-tag-chip connect">Connect</span>
            <span className="expo-tag-sep" aria-hidden="true">|</span>
            <span className="expo-tag-chip discover">Discover</span>
            <span className="expo-tag-sep" aria-hidden="true">|</span>
            <span className="expo-tag-chip elevate">Elevate</span>
          </p>
        </motion.div>
      </header>

      <main className="expo-student-main">
        <div className="expo-student-layout">
          {/* Value highlights — desktop + mobile top */}
          <motion.aside
            className="expo-highlights"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div className="expo-hero-pill" variants={fadeUp} custom={0}>
              <FontAwesomeIcon icon={faWandMagicSparkles} className="expo-hero-pill-icon" />
              <span>Career Expo 2026 · Nairobi</span>
            </motion.div>

            <motion.h2 className="expo-hero-headline" variants={fadeUp} custom={1}>
              Your future <span className="starts">starts</span>{' '}
              <span className="here">here!</span>
            </motion.h2>

            <motion.div className="expo-free-banner" variants={fadeUp} custom={2}>
              <span className="expo-free-banner-glow" aria-hidden="true" />
              <strong>100% FREE</strong>
              <strong className="expo-free-banner-sub">for Jobseekers, Interns &amp; Employers</strong>
            </motion.div>

            <ul className="expo-highlight-list">
              {HIGHLIGHTS.map((item, i) => (
                <motion.li
                  key={item.title}
                  className={`expo-highlight-card accent-${item.accent}`}
                  variants={fadeUp}
                  custom={3 + i}
                  whileHover={{ scale: 1.02, y: -2 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <span className="expo-highlight-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.aside>

          {/* Registration card */}
          <motion.div
            className="expo-student-card"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="expo-student-card-shine" aria-hidden="true" />
            <div className="expo-student-card-top" aria-hidden="true" />
            <div className="expo-student-card-body">
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    className="expo-student-success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      className="expo-student-success-icon"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </motion.div>
                    <h2>You&apos;re registered!</h2>
                    <p>{successMessage}</p>
                    <Link to="/login" className="expo-student-login-link">
                      Go to Login
                    </Link>
                    <p className="expo-student-footer-note" style={{ marginTop: '1.5rem' }}>
                      Didn&apos;t get the email? Check spam or use{' '}
                      <Link to="/forgot-password">Forgot Password</Link>.
                    </p>
                    <p className="expo-success-reset-note">
                      Ready for the next person in{' '}
                      <strong>{resetCountdown}s</strong>…
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h2 className="expo-form-title">Quick Register</h2>
                    <p className="expo-form-subtitle">
                      {form.type === 'employer'
                        ? 'Takes 30 seconds — we\u2019ll email you an activation link. Your employer account will be reviewed by our admin team before you can post jobs.'
                        : 'Takes 30 seconds — we\u2019ll email you an activation link to set your password.'}
                    </p>

                    <form className="expo-student-form" onSubmit={handleSubmit} noValidate>
                      <AnimatePresence>
                        {error && (
                          <motion.div
                            className="expo-student-error"
                            role="alert"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            {error}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {[
                        { id: 'expo-name', name: 'name', type: 'text', label: 'Full name', icon: faUser, auto: 'name', ph: 'Your full name' },
                        { id: 'expo-email', name: 'email', type: 'email', label: 'Email', icon: faEnvelope, auto: 'email', ph: 'you@example.com' },
                        { id: 'expo-phone', name: 'phone', type: 'tel', label: 'Phone number', icon: faPhone, auto: 'tel', ph: '+254 7XX XXX XXX' },
                      ].map((field, idx) => (
                        <div key={field.name} className="expo-student-field">
                          <label htmlFor={field.id}>{field.label}</label>
                          <div className="expo-input-wrap">
                            <span className="expo-input-icon-slot" aria-hidden="true">
                              <FontAwesomeIcon icon={field.icon} className="expo-input-icon" />
                            </span>
                            <input
                              id={field.id}
                              name={field.name}
                              type={field.type}
                              required
                              autoComplete={field.auto}
                              placeholder={field.ph}
                              value={form[field.name]}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      ))}

                      <div className="expo-type-section">
                        <label htmlFor="expo-type-select" className="expo-student-type-label">
                          I am a
                        </label>

                        <div className="expo-type-select-wrap">
                          <span className="expo-type-select-icon" aria-hidden="true">
                            <FontAwesomeIcon icon={getTypeIcon(form.type)} />
                          </span>
                          <select
                            id="expo-type-select"
                            name="type"
                            className="expo-type-select"
                            value={form.type}
                            onChange={handleChange}
                          >
                            {ACCOUNT_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="expo-student-submit"
                        disabled={loading}
                      >
                        <span className="expo-submit-shimmer" aria-hidden="true" />
                        {loading ? (
                          <span className="expo-submit-loading">
                            <span className="expo-spinner" />
                            Registering…
                          </span>
                        ) : (
                          'Register Now — 100% Free'
                        )}
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ExpoStudentRegistration;
